/* eslint-disable no-unused-vars */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Button, Flex, Text, useDisclosure } from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormValidationToast } from '../../../../services/actionToast';
import { InputForm } from '../../../inputs/forms/InputForm';
import { ReturnButton } from '../../../buttons/ReturnButton';
import { useFetch } from '../../../../hooks/useFetch';
import { SwitchInputForm } from '../../../inputs/forms/SwitchInputForm';
import AlertModal from '../../../AlertModal';
import { PageTitle } from '../../../PageTitle';
import { FormBox } from '../../../formBox';
import { FormHeading } from '../../../formHeading';
import { TextAreaInputForm } from '../../../inputs/forms/TextAreaInputForm';
import { SelectInputForm } from '../../../inputs/forms/SelectInputForm';

interface ViewFormProps {
  defaultValues?: any;
}

interface FormInputsProps {
  name: string;
  value: any;
  placeholder?: string;
  label: string;
  yup: any;
}

export default function ViewForm({ defaultValues = {} }: ViewFormProps) {
  const validationToast = useFormValidationToast();
  const baseUrl = '/v1/admin/contents';
  const { requestApi, error } = useFetch(baseUrl, { actionFeedback: false });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { itemId, moduleId, weekId } = useParams();
  const navigate = useNavigate();

  const optionsUserType = [
    {
      label: 'Professor',
      value: 'PROFESSOR',
    },
    {
      label: 'Responsável',
      value: 'RESPONSAVEL',
    },
    {
      label: 'Todos',
      value: 'TODOS',
    },
  ];
  const yupschema: any = {};
  const defaultValuesSchema: any = {};

  const formInputs: FormInputsProps[] = [
    {
      name: 'name',
      value: defaultValues.name ?? '',
      placeholder: 'Nome do encontro',
      label: 'Nome',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'title',
      value: defaultValues.title ?? '',
      placeholder: 'Título do encontro',
      label: 'Título',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'user_type',
      value: defaultValues?.user_type
        ? optionsUserType.find((item) => item.value === defaultValues?.user_type)
        : optionsUserType.find((item) => item.value === 'TODOS'),
      placeholder: 'Selecione o tipo de usuário que receberá',
      label: 'Usuários que vão receber',
      yup: Yup.mixed(),
    },
    {
      name: 'description',
      value: defaultValues.description ?? '',
      placeholder: 'Descrição do encontro',
      label: 'Descrição',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'status',
      value: defaultValues.status ?? true,
      label: 'Status',
      yup: Yup.boolean(),
    },
  ];

  formInputs.forEach((object) => {
    yupschema[object.name] = object.yup;
    defaultValuesSchema[object.name] = object.value;
  });
  const schema = Yup.object().shape(yupschema);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValuesSchema,
  });

  const name = formInputs.find((field) => field.name === 'name');
  const title = formInputs.find((field) => field.name === 'title');
  const description = formInputs.find((field) => field.name === 'description');
  const status = formInputs.find((field) => field.name === 'status');
  const userType = formInputs.find((field) => field.name === 'user_type');

  const onSubmit = async (values: any) => {
    const params = {
      ...values,
      week_id: weekId,
      user_type: values.user_type.value,
    };
    await requestApi(
      defaultValues.name ? 'put' : 'post',
      params,
      defaultValues.name ? `${baseUrl}/${itemId}` : null
    );
    onOpen();
  };

  useEffect(() => {
    if (defaultValues.name) {
      reset(defaultValuesSchema);
    }
  }, [defaultValues]);

  return (
    <FormBox>
      <Flex>
        <ReturnButton
          previousPage={`contents/modules/${moduleId}/weeks/${weekId}/meetings`}
          mb={5}
        />
        <PageTitle>Visualizar Encontro</PageTitle>
      </Flex>

      <AlertModal
        alertMsg={
          <Text textAlign="center" fontWeight="bold" fontSize="25px">
            {!error
              ? `Encontro ${defaultValues.name ? 'editado' : 'criado'} com sucesso!`
              : `Erro ao ${defaultValues.name ? 'editar' : 'criar'} o encontro.`}
          </Text>
        }
        msgStatus={error ? 'error' : 'success'}
        closeOnOverlayClick={!!error}
        useSecondaryActions
        isOpenSecondary={isOpen}
        onCloseSecondary={onClose}
        ConfirmButton={
          <Button
            w="250px"
            boxShadow="0px 5px 10px #00000065"
            borderRadius="full"
            onClick={() => navigate(`/contents/modules/${moduleId}/weeks/${weekId}/meetings`)}
          >
            Retornar para a listagem
          </Button>
        }
      />

      <form noValidate onSubmit={handleSubmit(onSubmit, validationToast)}>
        <FormHeading>Informações Básicas</FormHeading>

        <Flex gap={5}>
          <Box flex={3}>
            <InputForm
              name={name?.name}
              label={name?.label}
              placeholder={name?.placeholder}
              isRequired
              isDisabled
              register={register}
              errors={errors}
            />
          </Box>

          <Box flex={3}>
            <InputForm
              name={title?.name}
              label={title?.label}
              placeholder={title?.placeholder}
              isRequired
              isDisabled
              register={register}
              errors={errors}
            />
          </Box>

          <Box flex={1}>
            <SwitchInputForm
              name={status?.name}
              label={status?.label}
              isDisabled
              errors={errors}
              control={control}
            />
          </Box>
        </Flex>
        <Box flex={1}>
          <SelectInputForm
            name={userType?.name}
            label={userType?.label}
            options={optionsUserType}
            isDisabled
            placeholder={userType?.placeholder}
            control={control}
            errors={errors}
          />
        </Box>
        <Box flex={1}>
          <TextAreaInputForm
            name={description?.name}
            label={description?.label}
            placeholder={description?.placeholder}
            isDisabled
            errors={errors}
            register={register}
          />
        </Box>
      </form>
    </FormBox>
  );
}
